import React from 'react';
import isEqual from 'lodash/isEqual';
import { isHiddenVariant } from '../../utilities/helperUtility';
import Alert from '../../components/Alert';
import EventsListRadioButtons from '../components/EventsListRadioButtons';
import CloseModalIcon from '../components/CloseModalIcon';
import PaymentOptions from '../components/PaymentOptions';
import Price from '../components/Price';
import ProductWidgetPage from '../ProductWidgetPage';
import TimeZone from '../../components/Common/TimeZone';
import Translation from '../../utilities/localizationUtility';
import { getTimeFrameFromDate } from '../../utilities/timeUtility';
import { stringToBoolean, getIntOrDefault } from '../../utilities/conversionUtility';
import UpdatedBookingPage from '../components/UpdatedBookingPage';
import ConfirmationPage from '../components/EventsListConfirmationPage';
import BrandingString from '../components/BrandingString';

export default class EventsListPage extends ProductWidgetPage {
  constructor(props) {
    super(props);

    this.state = {
      modalHeight: 100,
      isModalOpen: false,
      messages: [],
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onSlotSelect = this.onSlotSelect.bind(this);
  }

  componentDidUpdate(oldProps) {
    const {
      quantity,
      selectedLocation: nextSelectedLocation,
      selectedResource: nextSelectedResource,
      selectedVariant: nextSelectedVariant,
      setting,
      timeZone,
      orderActions: { setOrderDetails },
      frameActions: { fetchFrames },
      exceededBookingsLimit,
      exceededTrialPlan,
      variants,
    } = this.props;
    const {
      selectedLocation: prevSelectedLocation,
      selectedResource: prevSelectedResource,
      selectedVariant: prevSelectedVariant,
    } = oldProps;

    if (
      !isEqual(nextSelectedLocation, prevSelectedLocation)
      || !isEqual(nextSelectedVariant, prevSelectedVariant)
      || !isEqual(nextSelectedResource, prevSelectedResource)) {
      if (!exceededBookingsLimit && !exceededTrialPlan && !isHiddenVariant(variants, nextSelectedVariant)) {
        const maxMonth = getIntOrDefault(setting.max_number_of_months, 6);
        const { start, finish } = getTimeFrameFromDate(new Date(), timeZone, maxMonth);

        fetchFrames({
          start: start.format(),
          finish: finish.format(),
          resource_id: nextSelectedResource?.id,
          location_id: nextSelectedLocation?.id,
          isEventsList: true,
        });
      }

      const variant = variants.find((v) => v.external_id === nextSelectedVariant) || {};

      setOrderDetails({
        totalQuantity: quantity * (variant.unit_multiplier || 1),
        selectedTimeslot: null,
      });
    }
  }

  handleSubmit(e) {
    const {
      bookingFields,
      setting: { show_booking_fields },
      orderVariants,
      orderActions: { submitEventsListOrder },
    } = this.props;
    const showBookingFields = stringToBoolean(show_booking_fields);
    const messages = showBookingFields && bookingFields.length ? [] : this.getValidMessage();

    this.onSubmit({
      element: e,
      messages,
      itemsResponse: orderVariants,
      action: submitEventsListOrder,
      bookingFields,
    });
  }

  handleAddItems(reservation, callback) {
    const type = ['Date', 'Time', 'Location'];

    this.onAddItems({
      reservation,
      type,
      callback,
    });
  }

  onSlotSelect(timeslot) {
    const { orderActions: { setOrderDetails }, selectedTimeslot } = this.props;
    const timeslotId = timeslot ? timeslot.id : null;

    if (!selectedTimeslot || (selectedTimeslot.id !== timeslotId)) {
      setOrderDetails({
        available: (timeslot || {}).available,
        selectedTimeslot: timeslot,
      });
    }
  }

  render() {
    if (this.state.bookingUpdated) {
      return <UpdatedBookingPage />;
    }

    const {
      available,
      frames,
      loading,
      locale,
      locations,
      orderActions,
      orderVariants,
      productTitle,
      region,
      selectedLocation,
      selectedResource,
      selectedTimeslot,
      selectedVariant,
      setting,
      style,
      totalQuantity,
      variants,
      settingActions: { setUrlParam },
      shopDatepickerTimeInterval,
      shopDateFormat,
      timeZone,
      urlParams,
      brandingVisualized,
    } = this.props;
    const { modalHeight } = this.state;
    const canBook = selectedTimeslot && available >= totalQuantity
      && frames.length && variants.length && selectedVariant;
    const t = new Translation(this.props);
    const variant = variants.find((v) => v.external_id === selectedVariant) || {};
    const isVisibleVariant = !isHiddenVariant(variants, selectedVariant);
    const selectedTimeSlotWaitlist = selectedTimeslot ? selectedTimeslot.start : null;
    const isRenderWaitlistButton = this.showWaitlist() && selectedTimeslot && !canBook;

    return (
      <div className="center widget-container" style={{ minHeight: modalHeight + 20 }}>
        <CloseModalIcon />
        <Alert ref={this.setAlertRef} />
        <div className={`sub-panel ${setting.mode}`} style={{ ...style.body, ...style.boxShadow }}>
          <h3 className="text-center widget-title" style={style.title}>
            {t.key('title')}
          </h3>

          {this.renderExceededAlert()}
          {isVisibleVariant && this.renderLocationDropdown()}
          {isVisibleVariant && this.renderResourceDropdawn()}
          {this.renderVariantSelect()}
          {this.renderQuantityInput(available)}

          <PaymentOptions />

          {isVisibleVariant && (
            <EventsListRadioButtons
              frames={frames}
              getAvailability={this.getAvailability}
              loading={loading}
              locale={locale}
              onSlotSelect={this.onSlotSelect}
              region={region}
              selectedTimeslot={selectedTimeslot || {}}
              setting={setting}
              setUrlParam={setUrlParam}
              styled={style}
              timeZone={timeZone}
              translation={t}
              urlParams={urlParams}
              quantity={totalQuantity}
              variant={variant}
            />
          )}

          <TimeZone timeZone={timeZone} selectedTime={selectedTimeslot ? selectedTimeslot.start : null} />
          <Price title={`${t.key('total')}`} />

          {this.renderWaitlist(selectedTimeSlotWaitlist)}
          {isRenderWaitlistButton ? this.renderWaitlistButton() : this.renderButton(canBook, loading)}

          {this.renderModal(
            <ConfirmationPage
              alert={<Alert ref={this.setAlertRef} />}
              canBook={canBook}
              closeModal={this.closeModal ? this.closeModal : () => {}}
              dateFormat={shopDateFormat}
              enableLocationDropdown={this.getEnableLocationDropdown()}
              enableResourceDropdown={this.getEnableResourceDropdown()}
              getValidationFn={this.handleGetValidationFn}
              locale={locale}
              locations={locations}
              orderActions={orderActions}
              productTitle={productTitle}
              renderSubmitButton={this.renderSubmitButton}
              selectedLocation={selectedLocation}
              selectedResource={selectedResource}
              setWrapperHeight={this.setWrapperHeight}
              setting={setting}
              style={style}
              timeInterval={shopDatepickerTimeInterval}
              timeSlotSelected={selectedTimeslot}
              timeZone={timeZone}
              translation={t}
              variants={orderVariants}
            />,
            brandingVisualized
          )}
          {brandingVisualized && <BrandingString />}
        </div>
      </div>
    );
  }
}
